import {
  ActionIcon,
  Button,
  Collapse,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Box,
  Badge,
  Image,
  Tooltip,
  Textarea,
  FileButton,
  Select,
  Switch,
  Alert,
  MultiSelect,
  Loader,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import {
  IconCheck,
  IconChevronRight,
  IconInfoCircle,
  IconCopy,
  IconX,
  IconTrash,
  IconUpload,
  IconPhone,
  IconBrandTeams,
  IconDownload,
  IconTicket,
} from '@tabler/icons-react';
import { Fragment, useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ZendeskOAuth } from '../../ZendeskOAuth';

import {
  getIntegrations,
  createLineIntegration,
  deleteLineIntegration,
  createWowTalkIntegration,
  deleteWowTalkIntegration,
  createLineWorksIntegration,
  deleteLineWorksIntegration,
  createTwilioIntegration,
  deleteTwilioIntegration,
  createLarkIntegration,
  updateLarkIntegration,
  deleteLarkIntegration,
  createTeamsIntegration,
  deleteTeamsIntegration,
  downloadTeamsPackage,
  LineIntegrationConfig,
  WowTalkIntegrationConfig,
  LineWorksIntegrationConfig,
  TwilioIntegrationConfig,
  LarkIntegrationConfig,
  TeamsIntegrationConfig,
  IntegrationItem,
  PortersIntegrationConfig,
  PortersConfigDetail,
  getPortersConfig,
  savePortersConfig,
  deletePortersConfig,
  testPortersConnection,
  buildPortersAuthorizeUrl,
  useBot,
  copyToClipboard,
  getZendeskConnections,
  createZendeskIntegration,
  deleteZendeskIntegration,
  patchZendeskIntegration,
  testZendeskIntegration,
  registerZendeskWebhook,
  updateZendeskWebhookSecret,
  getZendeskForms,
  testZendeskSignature,
  ZendeskIntegrationConfig,
  ZendeskConnection,
  ZendeskConnectionSharer,
  ZendeskFormCoverage,
  ZendeskSignaturePreview,
} from '@mygpt/app';
import { useCopy } from '@/hooks/useCopy';

// 导入图标文件
import lineIcon from '@/assets/integration_icon/line.png';
import wowtalkIcon from '@/assets/integration_icon/wowtalk.png';
import lineworkIcon from '@/assets/integration_icon/linework.png';
import portersIcon from '@/assets/integration_icon/porters.png';
import discordIcon from '@/assets/integration_icon/discord.jpeg';
import telegramIcon from '@/assets/integration_icon/telegram.png';
import messengerIcon from '@/assets/integration_icon/messenger.png';
import instagramIcon from '@/assets/integration_icon/instagram.png';
import slackIcon from '@/assets/integration_icon/slack.png';
import larkIcon from '@/assets/integration_icon/lark.jpeg';
import whatsappIcon from '@/assets/integration_icon/whatsapp.png';
import larkbaseIcon from '@/assets/integration_icon/larkbase.png';

// 平台配置数据 - LINE、WowTalk、LINE WORKS、PORTERS 可配置，其他平台置灰
const platforms = [
  {
    name: 'LINE',
    value: 'line',
    icon: lineIcon,
    configurable: true,
  },
  {
    name: 'WowTalk',
    value: 'wowtalk',
    icon: wowtalkIcon,
    configurable: true,
  },
  {
    name: 'LINE WORKS',
    value: 'lineworks',
    icon: lineworkIcon,
    configurable: true,
  },
  {
    name: 'PORTERS',
    value: 'porters',
    icon: portersIcon,
    configurable: true,
  },
  {
    name: 'Twilio',
    value: 'twilio',
    icon: '',
    configurable: true,
  },
  {
    name: 'Lark',
    value: 'lark',
    icon: larkIcon,
    configurable: true,
  },
  {
    name: 'Microsoft Teams',
    value: 'teams',
    icon: '',
    configurable: true,
  },
  {
    name: 'Zendesk',
    value: 'zendesk',
    icon: '',
    configurable: true,
    // 公開先ではなく外部システム連携（回答ドラフト / 修正の学習 / ナレッジ取込 の用途をまとめて持つ接続）
    category: 'external' as const,
  },
  {
    name: 'Discord',
    value: 'discord',
    icon: discordIcon,
    configurable: false,
  },
  {
    name: 'Telegram',
    value: 'telegram',
    icon: telegramIcon,
    configurable: false,
  },
  {
    name: 'Messenger',
    value: 'messenger',
    icon: messengerIcon,
    configurable: false,
  },
  {
    name: 'Instagram',
    value: 'instagram',
    icon: instagramIcon,
    configurable: false,
  },
  {
    name: 'Slack',
    value: 'slack',
    icon: slackIcon,
    configurable: false,
  },
  {
    name: 'WhatsApp',
    value: 'whatsapp',
    icon: whatsappIcon,
    configurable: false,
  },
  {
    name: 'LarkBase',
    value: 'larkbase',
    icon: larkbaseIcon,
    configurable: false,
  },
];

interface ZendeskFormValues {
  connection_mode: 'existing' | 'new' | 'oauth';
  connection_id: string;
  subdomain: string;
  admin_email: string;
  api_token: string;
  friendly_name: string;
  target_form_ids: string;
  signature_patterns: string;
  draft_note_enabled: boolean;
  show_sources_in_note: boolean;
}

// "https://your-subdomain.zendesk.com/" / "your-subdomain.zendesk.com" → "your-subdomain"
const normalizeZendeskSubdomain = (raw: string) =>
  (raw || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .replace(/\.zendesk\.com$/, '');

const parseZendeskFormIds = (raw: string) =>
  (raw || '')
    .split(/[,\s、]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);

export const Publish = () => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const [copied, setCopied] = useCopy(false, 1000);
  const [lineModalOpened, { open: openLineModal, close: closeLineModal }] = useDisclosure(false);
  const [wowtalkModalOpened, { open: openWowTalkModal, close: closeWowTalkModal }] = useDisclosure(false);
  const [lineworksModalOpened, { open: openLineWorksModal, close: closeLineWorksModal }] = useDisclosure(false);
  const [portersModalOpened, { open: openPortersModal, close: closePortersModal }] = useDisclosure(false);
  const [twilioModalOpened, { open: openTwilioModal, close: closeTwilioModal }] = useDisclosure(false);
  const [larkModalOpened, { open: openLarkModal, close: closeLarkModal }] = useDisclosure(false);
  const [teamsModalOpened, { open: openTeamsModal, close: closeTeamsModal }] = useDisclosure(false);
  const [isEditingTeams, setIsEditingTeams] = useState(false);
  const [downloadingPackage, setDownloadingPackage] = useState(false);
  const [isEditingLark, setIsEditingLark] = useState(false);
  const [isEditingWowTalk, setIsEditingWowTalk] = useState(false);
  const [isEditingLineWorks, setIsEditingLineWorks] = useState(false);
  const [isEditingPorters, setIsEditingPorters] = useState(false);
  const [isEditingTwilio, setIsEditingTwilio] = useState(false);
  const [zendeskModalOpened, { open: openZendeskModal, close: closeZendeskModal }] = useDisclosure(false);
  const [isEditingZendesk, setIsEditingZendesk] = useState(false);
  const [zendeskConnections, setZendeskConnections] = useState<ZendeskConnection[]>([]);
  const [zendeskTesting, setZendeskTesting] = useState(false);
  const [zendeskSecretInput, setZendeskSecretInput] = useState('');
  // 対象フォームを名前で選ぶ（取れないときは ID 入力にフォールバック）
  const [zendeskForms, setZendeskForms] = useState<ZendeskFormCoverage | null>(null);
  const [zendeskFormsLoading, setZendeskFormsLoading] = useState(false);
  const [zendeskSigSample, setZendeskSigSample] = useState('');
  const [zendeskSigResult, setZendeskSigResult] = useState<ZendeskSignaturePreview | null>(null);
  const [zendeskPausing, setZendeskPausing] = useState(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [integrations, setIntegrations] = useState<IntegrationItem[]>([]);
  const [portersConfig, setPortersConfig] = useState<PortersConfigDetail | null>(null);
  const [portersTestResult, setPortersTestResult] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; platform: string } | null>(null);
  const [privateKeyFileName, setPrivateKeyFileName] = useState<string>('');
  const resetFileRef = useRef<() => void>(null);
  const [unconfiguredOpened, { toggle: toggleUnconfigured }] = useDisclosure(false);

  // Line 配置表单
  const lineForm = useForm<LineIntegrationConfig>({
    initialValues: {
      channel_id: '',
      channel_secret: '',
      channel_access_token: '',
    },
    validate: {
      channel_id: (value) => {
        if (!value) return t('publish.line.validation.channel_id_required');
        if (!/^\d{10}$/.test(value)) return t('publish.line.validation.channel_id_format');
        return null;
      },
      channel_secret: (value) => {
        if (!value) return t('publish.line.validation.channel_secret_required');
        if (!/^[a-f0-9]{32}$/.test(value)) return t('publish.line.validation.channel_secret_format');
        return null;
      },
      channel_access_token: (value) => {
        if (!value) return t('publish.line.validation.channel_access_token_required');
        if (!/^[A-Za-z0-9+/=._-]{170,}$/.test(value)) return t('publish.line.validation.channel_access_token_format');
        return null;
      },
    },
  });

  // WowTalk 配置表单
  const wowtalkForm = useForm<WowTalkIntegrationConfig>({
    initialValues: {
      company_id: '',
      from_account: '',
      from_password: '',
      api_base_url: '',
      api_key: '',
      callback_token: '',
    },
    validate: {
      company_id: (value) => {
        if (!value) return t('publish.wowtalk.validation.company_id_required');
        if (!/^[a-zA-Z0-9_-]+$/.test(value)) return t('publish.wowtalk.validation.company_id_format');
        return null;
      },
      from_account: (value) => {
        if (!value) return t('publish.wowtalk.validation.from_account_required');
        if (!/^[a-zA-Z0-9@._-]+$/.test(value)) return t('publish.wowtalk.validation.from_account_format');
        return null;
      },
      from_password: (value) => {
        if (!value) return t('publish.wowtalk.validation.from_password_required');
        if (value.length < 6) return t('publish.wowtalk.validation.from_password_length');
        return null;
      },
      api_base_url: (value) => {
        if (!value) return t('publish.wowtalk.validation.api_base_url_required');
        try {
          new URL(value);
        } catch {
          return t('publish.wowtalk.validation.api_base_url_format');
        }
        return null;
      },
      api_key: (value) => {
        if (!value) return t('publish.wowtalk.validation.api_key_required');
        if (value.length < 16) return t('publish.wowtalk.validation.api_key_length');
        if (!/^[\x21-\x7E]+$/.test(value)) return t('publish.wowtalk.validation.api_key_format');
        return null;
      },
      callback_token: (value) => {
        if (value && value.length < 16) return t('publish.wowtalk.validation.callback_token_length');
        if (value && !/^[\x21-\x7E]+$/.test(value)) return t('publish.wowtalk.validation.callback_token_format');
        return null;
      },
    },
  });

  // LINE WORKS 配置表单
  const lineworksForm = useForm<LineWorksIntegrationConfig>({
    initialValues: {
      client_id: '',
      client_secret: '',
      service_account: '',
      bot_id: '',
      private_key: '',
    },
    validate: {
      client_id: (value) => {
        if (!value) return t('publish.lineworks.validation.client_id_required');
        return null;
      },
      client_secret: (value) => {
        if (!value) return t('publish.lineworks.validation.client_secret_required');
        return null;
      },
      service_account: (value) => {
        if (!value) return t('publish.lineworks.validation.service_account_required');
        return null;
      },
      bot_id: (value) => {
        if (!value) return t('publish.lineworks.validation.bot_id_required');
        return null;
      },
      private_key: (value) => {
        if (!value) return t('publish.lineworks.validation.private_key_required');
        // 对齐后端 min_length=100 校验，前端先拦截，避免提交后才发现
        if (value.length < 100) return t('publish.lineworks.validation.private_key_too_short');
        return null;
      },
    },
  });

  // PORTERS 配置表单
  const portersForm = useForm<PortersIntegrationConfig>({
    initialValues: {
      porters_company_id: '',
      app_id: '',
      secret: '',
      api_host: 'api-hrbc-jp.porterscloud.com',
      scopes: 'partition_r,user_r',
      line_userid_field_alias: '',
    },
    validate: {
      porters_company_id: (value) => (value ? null : t('publish.porters.validation.company_id_required')),
      app_id: (value) => (value ? null : t('publish.porters.validation.app_id_required')),
      secret: (value) => (value ? null : t('publish.porters.validation.secret_required')),
      api_host: (value) => (value ? null : t('publish.porters.validation.api_host_required')),
    },
  });

  // Twilio 配置表单（电话受付，只存秘钥）
  const twilioForm = useForm<TwilioIntegrationConfig>({
    initialValues: {
      account_sid: '',
      auth_token: '',
      phone_number: '',
      friendly_name: '',
    },
    validate: {
      account_sid: (value) => {
        if (!value) return t('publish.twilio.validation.account_sid_required');
        if (!/^AC[a-zA-Z0-9]{30,}$/.test(value)) return t('publish.twilio.validation.account_sid_format');
        return null;
      },
      auth_token: (value) => {
        if (!value) return t('publish.twilio.validation.auth_token_required');
        return null;
      },
      phone_number: (value) => {
        if (!value) return t('publish.twilio.validation.phone_number_required');
        if (!/^\+?[1-9]\d{6,18}$/.test(value.replace(/[\s\-()]/g, '')))
          return t('publish.twilio.validation.phone_number_format');
        return null;
      },
    },
  });

  // Zendesk 回复环配置表单：接続二选一（复用已有 / 新建），api_token 为掩码字段，编辑时默认走复用分支不接触打码值
  const zendeskForm = useForm<ZendeskFormValues>({
    initialValues: {
      connection_mode: 'new',
      connection_id: '',
      subdomain: '',
      admin_email: '',
      api_token: '',
      friendly_name: '',
      target_form_ids: '',
      signature_patterns: '',
      draft_note_enabled: true,
      show_sources_in_note: true,
    },
    validate: {
      connection_id: (value, values) =>
        values.connection_mode === 'oauth' || (values.connection_mode === 'existing' && !value)
          ? t('publish.zendesk.validation.connection_required')
          : null,
      subdomain: (value, values) => {
        if (values.connection_mode !== 'new') return null;
        const s = normalizeZendeskSubdomain(value);
        if (!s) return t('publish.zendesk.validation.subdomain_required');
        if (!/^[a-z0-9][a-z0-9-]*$/.test(s)) return t('publish.zendesk.validation.subdomain_format');
        return null;
      },
      admin_email: (value, values) => {
        if (values.connection_mode !== 'new') return null;
        if (!value) return t('publish.zendesk.validation.admin_email_required');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return t('publish.zendesk.validation.admin_email_format');
        return null;
      },
      api_token: (value, values) =>
        values.connection_mode === 'new' && !value ? t('publish.zendesk.validation.api_token_required') : null,
      target_form_ids: (value) =>
        value &&
        value
          .split(/[,\s、]+/)
          .filter(Boolean)
          .some((x) => !/^\d+$/.test(x))
          ? t('publish.zendesk.validation.form_ids_format')
          : null,
    },
  });

  // Lark 通知配置表单（bot 级群机器人 webhook）
  const larkForm = useForm<LarkIntegrationConfig>({
    initialValues: {
      webhook_url: '',
      secret: '',
      app_id: '',
      app_secret: '',
      friendly_name: '',
    },
    validate: {
      webhook_url: (value, values) => {
        // 新建时：通知 webhook 与日历应用至少配置其一。
        // 编辑时：全部留空表示"保持原值"，不强制（后端对合并结果再校验）。
        if (!isEditingLark && !value && !values.app_id) return t('publish.lark.validation.at_least_one');
        if (
          value &&
          !value.trim().startsWith('https://open.larksuite.com/') &&
          !value.trim().startsWith('https://open.feishu.cn/')
        )
          return t('publish.lark.validation.webhook_format');
        return null;
      },
      app_secret: (value, values) =>
        !isEditingLark && values.app_id && !value ? t('publish.lark.validation.app_secret_required') : null,
    },
  });

  // Microsoft Teams 配置表单（一对一）
  const teamsForm = useForm<TeamsIntegrationConfig>({
    initialValues: {
      app_id: '',
      app_password: '',
      tenant_id: '',
      app_type: 'SingleTenant',
      display_name: '',
      accent_color: '#1F5C99',
      friendly_name: '',
    },
    validate: {
      app_id: (value) => (!value ? t('publish.teams.validation.app_id_required') : null),
      // Client Secret 为掩码字段，编辑（删后建）也需重新输入，故始终必填
      app_password: (value) => (!value ? t('publish.teams.validation.app_password_required') : null),
      tenant_id: (value, values) =>
        values.app_type === 'SingleTenant' && !value ? t('publish.teams.validation.tenant_id_required') : null,
    },
  });

  // 获取集成列表 (+ PORTERS 配置)
  const fetchIntegrations = async () => {
    if (!botId) return;
    try {
      setLoading(true);
      const [
        {
          data: { integrations },
        },
        portersData,
      ] = await Promise.all([
        getIntegrations(botId),
        // 无权限 (403) 等失败时视为无配置, 不能阻断整个设置页
        getPortersConfig(botId).catch(() => null),
      ]);
      setIntegrations(integrations || []);
      setPortersConfig(portersData);
    } catch (error) {
      console.error('获取集成列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 创建 Line 集成
  const handleCreateLineIntegration = async (values: LineIntegrationConfig) => {
    if (!botId) return;
    try {
      setSubmitting(true);
      await createLineIntegration(botId, values);
      closeLineModal();
      lineForm.reset();
      fetchIntegrations();
    } catch (error) {
      console.error('创建 Line 集成失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 创建或更新 WowTalk 集成
  const handleCreateWowTalkIntegration = async (values: WowTalkIntegrationConfig) => {
    if (!botId) return;
    try {
      setSubmitting(true);

      if (isEditingWowTalk) {
        // 编辑模式：先删除现有配置，再创建新配置
        const existingConfig = integrations.find((item) => item.platform === 'wowtalk');
        if (existingConfig) {
          await deleteWowTalkIntegration(botId, existingConfig.id);
        }
      }

      await createWowTalkIntegration(botId, values);
      closeWowTalkModal();
      wowtalkForm.reset();
      setIsEditingWowTalk(false);
      fetchIntegrations();
    } catch (error) {
      console.error('创建/更新 WowTalk 集成失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 创建或更新 LINE WORKS 集成
  const handleCreateLineWorksIntegration = async (values: LineWorksIntegrationConfig) => {
    if (!botId) return;
    try {
      setSubmitting(true);

      if (isEditingLineWorks) {
        const existingConfig = integrations.find((item) => item.platform === 'lineworks');
        if (existingConfig) {
          await deleteLineWorksIntegration(botId, existingConfig.id);
        }
      }

      await createLineWorksIntegration(botId, values);
      closeLineWorksModal();
      lineworksForm.reset();
      setIsEditingLineWorks(false);
      setPrivateKeyFileName('');
      fetchIntegrations();
    } catch (error: any) {
      console.error('创建/更新 LINE WORKS 集成失败:', error);
      // 后端对 private_key 等做校验，失败返回 400，需向用户提示并保留弹窗输入供修正
      const detail = error?.response?.data?.detail;
      showNotification({
        color: 'red',
        message: detail || t('errors.common'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // 创建或更新 Twilio 集成（电话受付）
  const handleCreateTwilioIntegration = async (values: TwilioIntegrationConfig) => {
    if (!botId) return;
    try {
      setSubmitting(true);

      if (isEditingTwilio) {
        const existingConfig = integrations.find((item) => item.platform === 'twilio');
        if (existingConfig) {
          await deleteTwilioIntegration(botId, existingConfig.id);
        }
      }

      await createTwilioIntegration(botId, values);
      closeTwilioModal();
      twilioForm.reset();
      setIsEditingTwilio(false);
      fetchIntegrations();
    } catch (error: any) {
      console.error('创建/更新 Twilio 集成失败:', error);
      // Twilio 凭证错误后端返 400 并带 detail（如凭据无效），需提示用户，避免静默失败
      const detail = error?.response?.data?.detail;
      showNotification({
        color: 'red',
        message: detail || t('errors.common'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // 打开 Twilio 配置弹窗
  const handleOpenTwilioModal = () => {
    const existingConfig = integrations.find((item) => item.platform === 'twilio');
    if (existingConfig) {
      // 编辑：account_sid/token 为敏感/掩码字段，需重新输入；仅回显号码与展示名
      setIsEditingTwilio(true);
      twilioForm.setValues({
        account_sid: '',
        auth_token: '',
        phone_number: existingConfig.phone_number || '',
        friendly_name: existingConfig.friendly_name || '',
      });
    } else {
      setIsEditingTwilio(false);
      twilioForm.reset();
    }
    openTwilioModal();
  };

  // ============ Zendesk ============
  const existingZendesk = integrations.find((item) => item.platform === 'zendesk');
  const zendeskWebhookUrl =
    existingZendesk?.webhook_url ||
    `${
      window.ENV?.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      'https://api-dev.gbase.ai'
    }/api/webhooks/zendesk/${botId}`;

  // 同じ接続を使う他 bot：選択中の接続 × いま入力中の対象フォームで重なりを判定（保存前に気づけるように）
  const zendeskSharers: ZendeskConnectionSharer[] = useMemo(() => {
    if (zendeskForm.values.connection_mode !== 'existing') return [];
    const conn = zendeskConnections.find((c) => c.id === zendeskForm.values.connection_id);
    const mine = new Set(parseZendeskFormIds(zendeskForm.values.target_form_ids).map(String));
    return (conn?.used_by || []).map((s) => {
      const theirs = new Set((s.target_form_ids || []).map(String));
      const overlap = mine.size === 0 || theirs.size === 0 || [...mine].some((f) => theirs.has(f));
      return { ...s, form_overlap: overlap };
    });
  }, [
    zendeskConnections,
    zendeskForm.values.connection_mode,
    zendeskForm.values.connection_id,
    zendeskForm.values.target_form_ids,
  ]);

  // フォーム一覧 + カバー範囲：接続が決まっているときだけ取れる（新規接続は保存後）
  const loadZendeskForms = async (connectionId?: string, selectedIds?: number[]) => {
    if (!botId || !connectionId) {
      setZendeskForms(null);
      return;
    }
    setZendeskFormsLoading(true);
    try {
      const { data } = await getZendeskForms(botId, {
        connection_id: connectionId,
        selected: (selectedIds || []).join(','),
      });
      setZendeskForms(data);
    } catch (error) {
      console.error('获取 Zendesk フォーム一覧失败:', error);
      setZendeskForms(null);
    } finally {
      setZendeskFormsLoading(false);
    }
  };

  useEffect(() => {
    if (!zendeskModalOpened) return;
    if (zendeskForm.values.connection_mode !== 'existing') {
      setZendeskForms(null);
      return;
    }
    loadZendeskForms(zendeskForm.values.connection_id, parseZendeskFormIds(zendeskForm.values.target_form_ids));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zendeskModalOpened, zendeskForm.values.connection_mode, zendeskForm.values.connection_id]);

  // カバー範囲は選択が変わるたびに手元で計算（API を叩き直さない）
  const zendeskCoverage = useMemo(() => {
    if (!zendeskForms) return null;
    const selected = new Set(parseZendeskFormIds(zendeskForm.values.target_form_ids));
    const all = selected.size === 0;
    let covered = 0;
    zendeskForms.forms.forEach((f) => {
      if (all || selected.has(f.id)) covered += f.recent_tickets;
    });
    if (all) covered += zendeskForms.recent_unknown_form;
    return { all, covered, total: zendeskForms.recent_total, unknown: zendeskForms.recent_unknown_form };
  }, [zendeskForms, zendeskForm.values.target_form_ids]);

  const handleZendeskSignatureTest = async () => {
    if (!botId || !zendeskSigSample.trim()) return;
    setZendeskTesting(true);
    try {
      const { data } = await testZendeskSignature(botId, {
        sample: zendeskSigSample,
        patterns: zendeskForm.values.signature_patterns
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setZendeskSigResult(data);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      showNotification({ color: 'red', message: detail || t('errors.common') });
    } finally {
      setZendeskTesting(false);
    }
  };

  // 用途「回答ドラフト投稿」の一時停止 / 再開：接続・webhook・公開済みナレッジには触らない
  const handleToggleZendeskPause = async () => {
    if (!botId || !existingZendesk) return;
    const paused = existingZendesk.purposes?.draft?.paused;
    setZendeskPausing(true);
    try {
      await patchZendeskIntegration(botId, existingZendesk.id, { status: paused ? 'active' : 'paused' });
      showNotification({
        color: 'green',
        message: paused ? t('publish.zendesk.purposes.resumed') : t('publish.zendesk.purposes.paused_done'),
      });
      fetchIntegrations();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      showNotification({ color: 'red', message: detail || t('errors.common') });
    } finally {
      setZendeskPausing(false);
    }
  };

  const handleOpenZendeskModal = async () => {
    setZendeskForms(null);
    setZendeskSigSample('');
    setZendeskSigResult(null);
    if (botId) {
      try {
        const { data } = await getZendeskConnections(botId);
        setZendeskConnections(data.connections || []);
      } catch (error) {
        console.error('获取 Zendesk 接続列表失败:', error);
        setZendeskConnections([]);
      }
    }
    if (existingZendesk) {
      setIsEditingZendesk(true);
      zendeskForm.setValues({
        connection_mode: 'existing',
        connection_id: existingZendesk.connection_id || '',
        subdomain: '',
        admin_email: '',
        api_token: '',
        friendly_name: existingZendesk.friendly_name || '',
        target_form_ids: (existingZendesk.target_form_ids || []).join(', '),
        signature_patterns: (existingZendesk.signature_patterns || []).join('\n'),
        draft_note_enabled: existingZendesk.draft_note_enabled ?? true,
        show_sources_in_note: existingZendesk.show_sources_in_note ?? true,
      });
    } else {
      setIsEditingZendesk(false);
      zendeskForm.reset();
    }
    setZendeskSecretInput('');
    openZendeskModal();
  };

  const handleCreateZendeskIntegration = async (values: ZendeskFormValues) => {
    if (!botId) return;
    try {
      setSubmitting(true);
      // 接続を変えない編集は PATCH（webhook を作り直さない、受信/投稿の履歴も残る）
      if (
        isEditingZendesk &&
        existingZendesk &&
        values.connection_mode === 'existing' &&
        values.connection_id === existingZendesk.connection_id
      ) {
        await patchZendeskIntegration(botId, existingZendesk.id, {
          target_form_ids: parseZendeskFormIds(values.target_form_ids),
          signature_patterns: values.signature_patterns
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean),
          draft_note_enabled: values.draft_note_enabled,
          show_sources_in_note: values.show_sources_in_note,
        });
        showNotification({ color: 'green', message: t('publish.zendesk.saved') });
        closeZendeskModal();
        zendeskForm.reset();
        setIsEditingZendesk(false);
        fetchIntegrations();
        return;
      }
      if (isEditingZendesk && existingZendesk) {
        await deleteZendeskIntegration(botId, existingZendesk.id);
      }
      const payload: ZendeskIntegrationConfig = {
        target_form_ids: parseZendeskFormIds(values.target_form_ids),
        signature_patterns: values.signature_patterns
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        draft_note_enabled: values.draft_note_enabled,
        show_sources_in_note: values.show_sources_in_note,
        register_webhook: true,
        ...(values.connection_mode === 'existing'
          ? { connection_id: values.connection_id }
          : {
              subdomain: normalizeZendeskSubdomain(values.subdomain),
              admin_email: values.admin_email.trim(),
              api_token: values.api_token.trim(),
              friendly_name: values.friendly_name.trim() || undefined,
            }),
      };
      const { data } = await createZendeskIntegration(botId, payload);
      if (data.form_overlap) {
        showNotification({
          color: 'yellow',
          message: t('publish.zendesk.shared.saved_overlap', {
            bots: (data.shared_with || []).map((s) => s.robot_name || s.robot_id).join('、'),
          }),
          autoClose: 10000,
        });
      }
      if (!data.webhook_registered) {
        // 配置已保存但 Zendesk 侧 webhook 自动登録失败：提示走弹窗里的手动兜底
        showNotification({
          color: 'yellow',
          message: t('publish.zendesk.webhook_error', { error: data.webhook_error || '' }),
          autoClose: 8000,
        });
      }
      closeZendeskModal();
      zendeskForm.reset();
      setIsEditingZendesk(false);
      fetchIntegrations();
    } catch (error: any) {
      console.error('创建/更新 Zendesk 集成失败:', error);
      const detail = error?.response?.data?.detail;
      showNotification({ color: 'red', message: detail || t('errors.common') });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestZendesk = async () => {
    if (!botId || !existingZendesk) return;
    setZendeskTesting(true);
    try {
      const result = await testZendeskIntegration(botId, existingZendesk.id);
      showNotification({ color: result.ok ? 'green' : 'red', message: result.message });
      fetchIntegrations();
    } finally {
      setZendeskTesting(false);
    }
  };

  const handleRegisterZendeskWebhook = async () => {
    if (!botId || !existingZendesk) return;
    setZendeskTesting(true);
    try {
      const result = await registerZendeskWebhook(botId, existingZendesk.id);
      showNotification({
        color: result.webhook_registered ? 'green' : 'red',
        message: result.webhook_registered
          ? t('publish.zendesk.webhook_registered_success')
          : result.webhook_error || t('errors.common'),
      });
      fetchIntegrations();
    } finally {
      setZendeskTesting(false);
    }
  };

  const handleSaveZendeskSecret = async () => {
    if (!botId || !existingZendesk || !zendeskSecretInput.trim()) return;
    setZendeskTesting(true);
    try {
      await updateZendeskWebhookSecret(botId, existingZendesk.id, { signing_secret: zendeskSecretInput.trim() });
      showNotification({ color: 'green', message: t('publish.zendesk.secret_saved') });
      setZendeskSecretInput('');
      fetchIntegrations();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      showNotification({ color: 'red', message: detail || t('errors.common') });
    } finally {
      setZendeskTesting(false);
    }
  };

  // 创建或更新 Lark 集成
  // 编辑走【合并更新】：留空字段保持原值（webhook 与日历应用是两组独立凭据，
  // 改一组不会清掉另一组）——不再用"删后建"，避免未重填的掩码字段被清空。
  const handleCreateLarkIntegration = async (values: LarkIntegrationConfig) => {
    if (!botId) return;
    try {
      setSubmitting(true);

      const existingConfig = integrations.find((item) => item.platform === 'lark');
      if (isEditingLark && existingConfig) {
        await updateLarkIntegration(botId, existingConfig.id, values);
      } else {
        await createLarkIntegration(botId, values);
      }
      closeLarkModal();
      larkForm.reset();
      setIsEditingLark(false);
      fetchIntegrations();
    } catch (error) {
      console.error('创建/更新 Lark 集成失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 打开 Lark 配置弹窗
  const handleOpenLarkModal = () => {
    const existingConfig = integrations.find((item) => item.platform === 'lark');
    if (existingConfig) {
      // 编辑：webhook/secret/app_secret 为掩码字段，需重新输入；app_id 回显（非密），展示名回显
      setIsEditingLark(true);
      larkForm.setValues({
        webhook_url: '',
        secret: '',
        app_id: '',
        app_secret: '',
        friendly_name: existingConfig.friendly_name || '',
      });
    } else {
      setIsEditingLark(false);
      larkForm.reset();
    }
    openLarkModal();
  };

  // 生成 Twilio Voice webhook URL：realtime 服务域名按当前环境判断
  // prod → prd-realtime，dev/staging/pre 等 → dev-realtime（不依赖后端、不加新环境变量）
  const generateTwilioWebhookUrl = () => {
    if (!botId) return '';
    const realtimeBase =
      import.meta.env.VITE_STAGE === 'production' ? 'https://prd-realtime.gbase.ai' : 'https://dev-realtime.gbase.ai';
    return `${realtimeBase}/twilio/voice?botId=${botId}`;
  };

  // ─── Microsoft Teams handlers ─────────────────────────────────────

  // 生成 Teams messaging endpoint（客户在 Azure Bot 侧填这个）
  const generateTeamsWebhookUrl = () => {
    if (!botId) return '';
    const existing = integrations.find((item) => item.platform === 'teams');
    if (existing?.webhook_url) return existing.webhook_url;
    const apiBaseUrl =
      window.ENV?.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      'https://api-dev.gbase.ai';
    return `${apiBaseUrl}/api/webhooks/teams/${botId}`;
  };

  // 打开 Teams 配置弹窗（新建/编辑）
  const handleOpenTeamsModal = () => {
    const existingConfig = integrations.find((item) => item.platform === 'teams');
    if (existingConfig) {
      // 编辑：app_password 为掩码字段需重新输入；其余回显
      setIsEditingTeams(true);
      teamsForm.setValues({
        app_id: existingConfig.app_id || '',
        app_password: '',
        tenant_id: existingConfig.tenant_id || '',
        app_type: existingConfig.app_type || 'SingleTenant',
        display_name: existingConfig.display_name || '',
        accent_color: existingConfig.accent_color || '#1F5C99',
        friendly_name: existingConfig.friendly_name || '',
      });
    } else {
      setIsEditingTeams(false);
      teamsForm.reset();
    }
    openTeamsModal();
  };

  // 创建或更新 Teams 集成（编辑 = 删后建）
  const handleCreateTeamsIntegration = async (values: TeamsIntegrationConfig) => {
    if (!botId) return;
    try {
      setSubmitting(true);
      if (isEditingTeams) {
        const existingConfig = integrations.find((item) => item.platform === 'teams');
        if (existingConfig) {
          await deleteTeamsIntegration(botId, existingConfig.id);
        }
      }
      await createTeamsIntegration(botId, values);
      closeTeamsModal();
      teamsForm.reset();
      setIsEditingTeams(false);
      fetchIntegrations();
    } catch (error: any) {
      console.error('创建/更新 Teams 集成失败:', error);
      const detail = error?.response?.data?.detail;
      showNotification({
        color: 'red',
        message: detail || t('errors.common'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // 下载 Teams 安装包 ZIP
  const handleDownloadTeamsPackage = async () => {
    if (!botId) return;
    const existingConfig = integrations.find((item) => item.platform === 'teams');
    if (!existingConfig) return;
    try {
      setDownloadingPackage(true);
      const name = existingConfig.display_name || 'GBase';
      await downloadTeamsPackage(botId, existingConfig.id, `${name}-Teams-App.zip`);
    } catch (error) {
      console.error('下载 Teams 安装包失败:', error);
      showNotification({ color: 'red', message: t('errors.common') });
    } finally {
      setDownloadingPackage(false);
    }
  };

  // ─── PORTERS handlers ─────────────────────────────────────────────

  // 打开 PORTERS 配置弹窗 (新建/编辑)
  const handleOpenPortersModal = () => {
    if (portersConfig) {
      setIsEditingPorters(true);
      portersForm.setValues({
        porters_company_id: portersConfig.porters_company_id || '',
        app_id: portersConfig.app_id || '',
        secret: portersConfig.secret || '',
        api_host: portersConfig.api_host || 'api-hrbc-jp.porterscloud.com',
        scopes: portersConfig.scopes || 'partition_r,user_r',
        line_userid_field_alias: portersConfig.line_userid_field_alias || '',
      });
    } else {
      setIsEditingPorters(false);
      portersForm.reset();
    }
    setPortersTestResult('');
    openPortersModal();
  };

  // 保存 PORTERS 配置 (保存后不关 Modal, 让用户立即看到"连接到 PORTERS"按钮)
  const handleSavePortersIntegration = async (values: PortersIntegrationConfig) => {
    if (!botId) return;
    try {
      setSubmitting(true);
      const saved = await savePortersConfig(botId, values);
      // 立刻更新本地 portersConfig 状态, 让 Modal 里的按钮条件生效
      setPortersConfig(saved);
      setIsEditingPorters(true);
      // 也异步刷新 list (后台静默)
      fetchIntegrations();
    } catch (error) {
      console.error('保存 PORTERS 配置失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 测试 PORTERS 连接 (调一次后端 /test)
  const handleTestPorters = async () => {
    if (!botId) return;
    try {
      setTesting(true);
      setPortersTestResult('');
      const result = await testPortersConnection(botId);
      setPortersTestResult(JSON.stringify(result, null, 2));
    } catch (error: any) {
      setPortersTestResult(`Error: ${error?.message || error}`);
    } finally {
      setTesting(false);
    }
  };

  // 在新标签页打开 PORTERS OAuth 授权 URL
  const handleConnectPorters = () => {
    if (!botId) return;
    const apiBaseUrl =
      window.ENV?.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      'https://api-dev.gbase.ai';
    const url = buildPortersAuthorizeUrl(botId, apiBaseUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // 删除集成 - 显示确认弹窗
  const handleDeleteIntegration = (configId: string, platform: string) => {
    setDeleteTarget({ id: configId, platform });
    openDeleteModal();
  };

  // 打开 WowTalk 配置弹窗
  const handleOpenWowTalkModal = () => {
    const existingConfig = integrations.find((item) => item.platform === 'wowtalk');

    if (existingConfig) {
      // 编辑现有配置 - 只回显非敏感字段
      setIsEditingWowTalk(true);
      wowtalkForm.setValues({
        company_id: existingConfig.company_id || '',
        from_account: existingConfig.from_account || '',
        from_password: '', // 敏感字段置空，用户需要重新输入
        api_base_url: existingConfig.api_base_url || '',
        api_key: '', // 敏感字段置空，用户需要重新输入
        callback_token: '', // 敏感字段置空，用户需要重新输入
      });
    } else {
      // 新建配置
      setIsEditingWowTalk(false);
      wowtalkForm.reset();
    }

    openWowTalkModal();
  };

  // 打开 LINE WORKS 配置弹窗
  const handleOpenLineWorksModal = () => {
    const existingConfig = integrations.find((item) => item.platform === 'lineworks');

    if (existingConfig) {
      setIsEditingLineWorks(true);
      lineworksForm.setValues({
        client_id: existingConfig.client_id || '',
        client_secret: '', // 敏感字段置空
        service_account: existingConfig.service_account || '',
        bot_id: existingConfig.bot_id || '',
        private_key: '', // 敏感字段置空
      });
      setPrivateKeyFileName('');
    } else {
      setIsEditingLineWorks(false);
      lineworksForm.reset();
      setPrivateKeyFileName('');
    }

    openLineWorksModal();
  };

  // 处理私钥文件上传
  const handlePrivateKeyFileUpload = (file: File | null) => {
    if (!file) return;
    setPrivateKeyFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      // 将换行符替换为 \n 字符串存储
      const singleLineKey = content.replace(/\r\n/g, '\n');
      lineworksForm.setFieldValue('private_key', singleLineKey);
    };
    reader.readAsText(file);
  };

  // 确认删除集成
  const confirmDeleteIntegration = async () => {
    if (!botId || !deleteTarget) return;
    try {
      if (deleteTarget.platform === 'line') {
        await deleteLineIntegration(botId, deleteTarget.id);
      } else if (deleteTarget.platform === 'wowtalk') {
        await deleteWowTalkIntegration(botId, deleteTarget.id);
      } else if (deleteTarget.platform === 'lineworks') {
        await deleteLineWorksIntegration(botId, deleteTarget.id);
      } else if (deleteTarget.platform === 'porters') {
        await deletePortersConfig(botId);
      } else if (deleteTarget.platform === 'twilio') {
        await deleteTwilioIntegration(botId, deleteTarget.id);
      } else if (deleteTarget.platform === 'lark') {
        await deleteLarkIntegration(botId, deleteTarget.id);
      } else if (deleteTarget.platform === 'teams') {
        await deleteTeamsIntegration(botId, deleteTarget.id);
      } else if (deleteTarget.platform === 'zendesk') {
        await deleteZendeskIntegration(botId, deleteTarget.id);
      }
      fetchIntegrations();
      closeDeleteModal();
      setDeleteTarget(null);
    } catch (error) {
      console.error('删除集成失败:', error);
    }
  };

  // 生成 Webhook URL
  const generateWebhookUrl = () => {
    if (!botId) return '';
    // 检查是否已有配置，如果有则显示实际的 webhook_url
    const existingIntegration = integrations.find((item) => item.platform === 'line');
    if (existingIntegration) {
      return existingIntegration.webhook_url;
    }
    // 如果没有配置，根据环境变量生成预期的 webhook URL
    const apiBaseUrl =
      window.ENV.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      'https://api-dev.gbase.ai';
    return `${apiBaseUrl}/api/webhooks/line/${botId}`;
  };

  // 生成 WowTalk Callback URL
  const generateCallbackUrl = () => {
    if (!botId) return '';
    // 检查是否已有配置，如果有则显示实际的 callback_url
    const existingIntegration = integrations.find((item) => item.platform === 'wowtalk');
    if (existingIntegration) {
      return existingIntegration.callback_url;
    }
    // 如果没有配置，根据环境变量生成预期的 callback URL
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api-dev.gbase.ai';
    return `${apiBaseUrl}/api/webhooks/wowtalk/${botId}`;
  };

  // 生成 LINE WORKS Webhook URL
  const generateLineWorksWebhookUrl = () => {
    if (!botId) return '';
    const existingIntegration = integrations.find((item) => item.platform === 'lineworks');
    if (existingIntegration) {
      return existingIntegration.webhook_url;
    }
    const apiBaseUrl =
      window.ENV.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      'https://api-dev.gbase.ai';
    return `${apiBaseUrl}/api/webhooks/lineworks/${botId}`;
  };

  // 打开配置弹窗的统一处理
  const handleOpenConfigModal = (platformValue: string) => {
    if (platformValue === 'line') {
      openLineModal();
    } else if (platformValue === 'wowtalk') {
      handleOpenWowTalkModal();
    } else if (platformValue === 'lineworks') {
      handleOpenLineWorksModal();
    } else if (platformValue === 'porters') {
      handleOpenPortersModal();
    } else if (platformValue === 'twilio') {
      handleOpenTwilioModal();
    } else if (platformValue === 'lark') {
      handleOpenLarkModal();
    } else if (platformValue === 'teams') {
      handleOpenTeamsModal();
    } else if (platformValue === 'zendesk') {
      handleOpenZendeskModal();
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, [botId]);

  if (!botId) {
    return (
      <div
        className="relative rounded-xl bg-white flex-1 p-6"
        style={{
          boxShadow: '0px 10px 60px 0px rgba(226, 236, 249, 0.50)',
        }}
      >
        <Text c="dimmed">{t('publish.loading')}</Text>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-xl bg-white flex-1 p-6"
      style={{
        boxShadow: '0px 10px 60px 0px rgba(226, 236, 249, 0.50)',
      }}
    >
      <Stack gap="lg">
        {/* 标题和说明 */}
        <Box>
          <Group gap="xs" mb="sm">
            <Text size="lg">{t('publish.title')}</Text>
            <Text c="red" size="lg">
              *
            </Text>
          </Group>

          <Text size="sm" c="gray.6">
            {t('publish.disclaimer')}
          </Text>
        </Box>

        {/* 平台表格 */}
        <Box>
          <Group justify="space-between" mb="md">
            <Text c="gray.7">{t('publish.platform_title')}</Text>
            <Group gap="lg">
              <Group gap="xs">
                <Text size="sm" c="gray.6">
                  {t('publish.user_billing.label')}
                </Text>
                <Text size="sm">{t('publish.user_billing.status')}</Text>
                <Tooltip
                  label={
                    <Box>
                      <Text size="sm" mb="xs">
                        {t('publish.user_billing.tooltip.title')}
                      </Text>
                      <Text size="xs">{t('publish.user_billing.tooltip.description')}</Text>
                      <Text size="xs" mt="xs">
                        {t('publish.user_billing.tooltip.sponsored')}
                      </Text>
                    </Box>
                  }
                  multiline
                  w={280}
                  position="top"
                  withArrow
                >
                  <ActionIcon size="md" variant="subtle" color="gray">
                    <IconInfoCircle size="1rem" />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          </Group>
          {(() => {
            // porters 用 portersConfig 判断配置状态，其余平台用 integrations 判断
            const isPlatformConfigured = (platform: (typeof platforms)[number]) =>
              platform.value === 'porters'
                ? portersConfig != null && portersConfig.status !== 'disabled'
                : integrations.some((item) => item.platform === platform.value);

            // Zendesk 一期灰度：部署侧显式 HIDE 时不展示（后端另有 ZENDESK_INTEGRATION_ENABLED 总开关）
            const visiblePlatforms =
              window.ENV?.GBASE_SUPPORT_FRONTEND_HIDE_ZENDESK === 'true'
                ? platforms.filter((p) => p.value !== 'zendesk')
                : platforms;
            // 外部システム連携（Zendesk）は公開先の後ろに見出し付きでまとめる
            const byCategory = (a: (typeof platforms)[number], b: (typeof platforms)[number]) =>
              Number(!!(a as any).category) - Number(!!(b as any).category);
            const configuredPlatforms = visiblePlatforms.filter((p) => isPlatformConfigured(p)).sort(byCategory);
            const unconfiguredPlatforms = visiblePlatforms.filter((p) => !isPlatformConfigured(p)).sort(byCategory);
            const isExternal = (p: (typeof platforms)[number]) => (p as any).category === 'external';
            const externalHeading = (
              <Group gap="xs" mt="xs">
                <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: '#03363D' }} />
                <Text size="sm" c="dimmed">
                  {t('publish.section.external')}
                </Text>
              </Group>
            );

            // 平台图标：twilio/teams 没有图片，用品牌图标
            const renderPlatformIcon = (platform: (typeof platforms)[number], size: number, radius: string) =>
              platform.value === 'twilio' ? (
                <IconPhone size={size} color="#F22F46" />
              ) : platform.value === 'teams' ? (
                <IconBrandTeams size={size} color="#5059C9" />
              ) : platform.value === 'zendesk' ? (
                <IconTicket size={size} color="#03363D" />
              ) : (
                <Image src={platform.icon} alt={platform.name} w={size} h={size} style={{ borderRadius: radius }} />
              );

            // 平台说明 tooltip（line / twilio / porters）
            const renderPlatformTooltip = (platform: (typeof platforms)[number]) => {
              const labelMap: Record<string, string> = {
                line: t('publish.line.tooltip'),
                twilio: t('publish.twilio.tooltip'),
                zendesk: t('publish.zendesk.tooltip'),
                porters: t('publish.porters.tooltip', 'PORTERS HRBC 員工信息接入聊天上下文'),
              };
              const label = labelMap[platform.value];
              if (!label) return null;
              return (
                <Tooltip label={label} position="right" withArrow>
                  <ActionIcon size="sm" variant="subtle" color="gray">
                    <IconInfoCircle size="0.875rem" />
                  </ActionIcon>
                </Tooltip>
              );
            };

            return (
              <Stack gap="md">
                {configuredPlatforms.length > 0 && (
                  <>
                    <Group gap="xs">
                      <Box
                        w={8}
                        h={8}
                        style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-green-6)' }}
                      />
                      <Text size="sm" c="dimmed">
                        {t('publish.section.connected', { count: configuredPlatforms.length })}
                      </Text>
                    </Group>
                    {configuredPlatforms.map((platform) => {
                      const isPorters = platform.value === 'porters';
                      const integration = integrations.find((item) => item.platform === platform.value);

                      // PORTERS 的状态徽章颜色按 active/pending/error 区分
                      const portersBadgeColor =
                        portersConfig?.status === 'active'
                          ? 'green'
                          : portersConfig?.status === 'error'
                          ? 'red'
                          : portersConfig?.status === 'pending'
                          ? 'yellow'
                          : 'gray';
                      const portersBadgeLabel =
                        portersConfig?.status === 'active'
                          ? t('publish.porters.status.active', '已连接')
                          : portersConfig?.status === 'error'
                          ? t('publish.porters.status.error', '错误')
                          : portersConfig?.status === 'pending'
                          ? t('publish.porters.status.pending', '待激活')
                          : t('publish.status.configured');

                      // Zendesk：用途「回答ドラフト」の状態（一時停止 / 試運転）をカードでも見せる
                      const zendeskPurpose = platform.value === 'zendesk' ? integration?.purposes?.draft : undefined;
                      const zendeskPaused = !!zendeskPurpose?.paused;
                      return (
                        <Fragment key={platform.value}>
                          {isExternal(platform) && externalHeading}
                          <Box
                            p="md"
                            style={{
                              border: `1px solid var(--mantine-color-${zendeskPaused ? 'yellow' : 'green'}-2)`,
                              borderRadius: 'var(--mantine-radius-md)',
                              backgroundColor: `var(--mantine-color-${zendeskPaused ? 'yellow' : 'green'}-0)`,
                            }}
                          >
                            <Group justify="space-between">
                              <Group gap="sm">
                                {renderPlatformIcon(platform, 28, '6px')}
                                <div>
                                  <Group gap="xs">
                                    <Text fw={500}>{platform.name}</Text>
                                    {isPorters ? (
                                      <Badge color={portersBadgeColor} variant="light" size="sm">
                                        {portersBadgeLabel}
                                      </Badge>
                                    ) : zendeskPurpose ? (
                                      <Badge
                                        color={zendeskPaused ? 'yellow' : zendeskPurpose.dry_run ? 'grape' : 'green'}
                                        variant="light"
                                        size="sm"
                                      >
                                        {zendeskPaused
                                          ? t('publish.zendesk.purposes.paused')
                                          : zendeskPurpose.dry_run
                                          ? t('publish.zendesk.purposes.dry_run')
                                          : t('publish.status.configured')}
                                      </Badge>
                                    ) : (
                                      <Badge color="green" variant="light" size="sm">
                                        {t('publish.status.configured')}
                                      </Badge>
                                    )}
                                    {renderPlatformTooltip(platform)}
                                  </Group>
                                  {platform.value === 'zendesk' && integration?.purposes && (
                                    <Text size="xs" c="dimmed" mt={2}>
                                      {t('publish.zendesk.card_purposes', {
                                        learn: integration.purposes.learn_edits?.enabled
                                          ? t('publish.zendesk.purposes.on')
                                          : t('publish.zendesk.purposes.off'),
                                        datasets: integration.purposes.import.length,
                                      })}
                                    </Text>
                                  )}
                                </div>
                              </Group>
                              <Group gap="xs">
                                {(integration || (isPorters && portersConfig)) && (
                                  <Button
                                    variant="outline"
                                    size="xs"
                                    color="red"
                                    onClick={() =>
                                      handleDeleteIntegration(
                                        isPorters ? portersConfig!.bot_id : integration!.id,
                                        platform.value
                                      )
                                    }
                                  >
                                    {t('publish.actions.delete')}
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="xs"
                                  onClick={() => handleOpenConfigModal(platform.value)}
                                >
                                  {t('publish.actions.configure')}
                                </Button>
                              </Group>
                            </Group>
                          </Box>
                        </Fragment>
                      );
                    })}
                  </>
                )}

                {unconfiguredPlatforms.length > 0 && (
                  <>
                    <Group gap="xs" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={toggleUnconfigured}>
                      <IconChevronRight
                        size={14}
                        style={{
                          transform: unconfiguredOpened ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                        }}
                        color="var(--mantine-color-gray-5)"
                      />
                      <Text size="sm" c="dimmed">
                        {t('publish.section.unconfigured', { count: unconfiguredPlatforms.length })}
                      </Text>
                    </Group>
                    <Collapse in={unconfiguredOpened}>
                      <Stack gap="xs">
                        {unconfiguredPlatforms.map((platform) => (
                          <Fragment key={platform.value}>
                            {isExternal(platform) && externalHeading}
                            <Box
                              px="md"
                              py="sm"
                              style={{
                                border: '1px solid var(--mantine-color-gray-2)',
                                borderRadius: 'var(--mantine-radius-md)',
                                opacity: platform.configurable ? 1 : 0.5,
                              }}
                            >
                              <Group justify="space-between">
                                <Group gap="sm">
                                  {renderPlatformIcon(platform, 24, '4px')}
                                  <Text size="sm">{platform.name}</Text>
                                  {renderPlatformTooltip(platform)}
                                </Group>
                                <Button
                                  variant="outline"
                                  size="xs"
                                  color={platform.configurable ? 'blue' : 'gray'}
                                  disabled={!platform.configurable}
                                  onClick={() => platform.configurable && handleOpenConfigModal(platform.value)}
                                >
                                  {t('publish.actions.configure')}
                                </Button>
                              </Group>
                            </Box>
                          </Fragment>
                        ))}
                      </Stack>
                    </Collapse>
                  </>
                )}
              </Stack>
            );
          })()}
        </Box>
      </Stack>

      {/* LINE 配置弹窗 - 简化设计 */}
      <Modal
        opened={lineModalOpened}
        onClose={closeLineModal}
        title={
          <Group justify="space-between" w="100%">
            <Text size="lg">{t('publish.line.modal_title')}</Text>
            <ActionIcon variant="subtle" color="gray" onClick={closeLineModal}>
              <IconX size="1rem" />
            </ActionIcon>
          </Group>
        }
        size="md"
        centered
        withCloseButton={false}
        styles={{
          title: { width: '100%' },
        }}
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.6">
            {t('publish.line.modal_description')}
          </Text>

          <Text size="sm" c="blue.6">
            <Text
              component="a"
              href="https://docs.gbase.ai/user-manual/welcome/mybot/setting/publish-line"
              target="_blank"
              td="underline"
            >
              {t('publish.line.manual_link')}
            </Text>
          </Text>

          <form onSubmit={lineForm.onSubmit(handleCreateLineIntegration)}>
            <Stack gap="lg">
              {/* Step 1: Webhook URL */}
              <Stack gap="sm">
                <Group gap="xs">
                  <Box
                    w={20}
                    h={20}
                    bg="blue.6"
                    c="white"
                    style={{
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    1
                  </Box>
                  <Text size="sm">{t('publish.line.webhook_step')}</Text>
                </Group>

                <Stack gap="xs">
                  <Text size="sm">{t('publish.line.webhook_label')}</Text>
                  <Group gap="xs" align="center">
                    <Text
                      size="sm"
                      c="gray.7"
                      style={{
                        fontFamily: 'monospace',
                        backgroundColor: '#f8f9fa',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef',
                        flex: 1,
                        wordBreak: 'break-all',
                      }}
                    >
                      {generateWebhookUrl()}
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => {
                        const copied = copyToClipboard(generateWebhookUrl());
                        setCopied(!!copied);
                      }}
                    >
                      {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                    </ActionIcon>
                  </Group>
                </Stack>
              </Stack>

              {/* Step 2: LINE Credentials */}
              <Stack gap="sm">
                <Group gap="xs">
                  <Box
                    w={20}
                    h={20}
                    bg="blue.6"
                    c="white"
                    style={{
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    2
                  </Box>
                  <Text size="sm">{t('publish.line.credentials_step')}</Text>
                </Group>

                <Stack gap="md">
                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.line.channel_id')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.line.channel_id_description')}
                    {...lineForm.getInputProps('channel_id')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.line.channel_secret')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.line.channel_secret_description')}
                    {...lineForm.getInputProps('channel_secret')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.line.channel_access_token')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.line.channel_access_token_description')}
                    {...lineForm.getInputProps('channel_access_token')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />
                </Stack>
              </Stack>

              <Group justify="flex-end" mt="lg">
                <Button type="submit" loading={submitting}>
                  {t('publish.line.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      {/* Microsoft Teams 配置弹窗（一对一） */}
      <Modal
        opened={teamsModalOpened}
        onClose={closeTeamsModal}
        title={
          <Group justify="space-between" w="100%">
            <Text size="lg">
              {isEditingTeams
                ? `${t('publish.teams.modal_title')} - ${t('publish.actions.edit')}`
                : t('publish.teams.modal_title')}
            </Text>
            <ActionIcon variant="subtle" color="gray" onClick={closeTeamsModal}>
              <IconX size="1rem" />
            </ActionIcon>
          </Group>
        }
        size="md"
        centered
        withCloseButton={false}
        styles={{ title: { width: '100%' } }}
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.6">
            {t('publish.teams.modal_description')}
          </Text>

          <form onSubmit={teamsForm.onSubmit(handleCreateTeamsIntegration)}>
            <Stack gap="lg">
              {/* Step 1: Azure 凭据 */}
              <Stack gap="sm">
                <Group gap="xs">
                  <Box
                    w={20}
                    h={20}
                    bg="blue.6"
                    c="white"
                    style={{
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    1
                  </Box>
                  <Text size="sm">{t('publish.teams.credentials_step')}</Text>
                </Group>

                <Stack gap="md">
                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.teams.app_id')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.teams.app_id_description')}
                    {...teamsForm.getInputProps('app_id')}
                    styles={{ input: { fontSize: '14px' } }}
                  />
                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.teams.app_password')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.teams.app_password_description')}
                    placeholder={isEditingTeams ? t('publish.teams.app_password_edit_placeholder') : ''}
                    {...teamsForm.getInputProps('app_password')}
                    styles={{ input: { fontSize: '14px' } }}
                  />
                  <Select
                    label={t('publish.teams.app_type')}
                    data={[
                      { value: 'SingleTenant', label: 'Single Tenant' },
                      { value: 'MultiTenant', label: 'Multi Tenant' },
                    ]}
                    {...teamsForm.getInputProps('app_type')}
                  />
                  <TextInput
                    label={
                      teamsForm.values.app_type === 'SingleTenant' ? (
                        <Group gap="xs">
                          <Text size="sm">{t('publish.teams.tenant_id')}</Text>
                          <Text c="red" size="sm">
                            *
                          </Text>
                        </Group>
                      ) : (
                        <Text size="sm">{t('publish.teams.tenant_id')}</Text>
                      )
                    }
                    description={t('publish.teams.tenant_id_description')}
                    {...teamsForm.getInputProps('tenant_id')}
                    styles={{ input: { fontSize: '14px' } }}
                  />
                </Stack>
              </Stack>

              {/* Step 2: 展示设置 */}
              <Stack gap="sm">
                <Group gap="xs">
                  <Box
                    w={20}
                    h={20}
                    bg="blue.6"
                    c="white"
                    style={{
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    2
                  </Box>
                  <Text size="sm">{t('publish.teams.display_step')}</Text>
                </Group>
                <Group grow>
                  <TextInput
                    label={t('publish.teams.display_name')}
                    placeholder="GBase"
                    {...teamsForm.getInputProps('display_name')}
                    styles={{ input: { fontSize: '14px' } }}
                  />
                  <TextInput
                    label={t('publish.teams.accent_color')}
                    placeholder="#1F5C99"
                    {...teamsForm.getInputProps('accent_color')}
                    styles={{ input: { fontSize: '14px' } }}
                  />
                </Group>
                <Text size="xs" c="orange.7">
                  {t('publish.teams.repackage_note')}
                </Text>
              </Stack>

              {/* Step 3: messaging endpoint（客户在 Azure Bot 侧填这个） */}
              <Stack gap="xs">
                <Text size="sm">{t('publish.teams.endpoint_label')}</Text>
                <Group gap="xs" align="center">
                  <Text
                    size="sm"
                    c="gray.7"
                    style={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f8f9fa',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef',
                      flex: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {generateTeamsWebhookUrl()}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    onClick={() => {
                      const ok = copyToClipboard(generateTeamsWebhookUrl());
                      setCopied(!!ok);
                    }}
                  >
                    {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                  </ActionIcon>
                </Group>
              </Stack>

              {/* Step 4: 安装包下载 + 管理员手续（仅已配置时） */}
              {integrations.some((item) => item.platform === 'teams') && (
                <Stack gap="sm">
                  <Text size="sm">{t('publish.teams.package_step')}</Text>
                  <Button
                    variant="light"
                    leftSection={<IconDownload size="1rem" />}
                    loading={downloadingPackage}
                    onClick={handleDownloadTeamsPackage}
                  >
                    {t('publish.teams.download_package')}
                  </Button>
                  <Text size="xs" c="gray.6" style={{ whiteSpace: 'pre-line' }}>
                    {t('publish.teams.admin_steps')}
                  </Text>
                  <Text size="xs" c="orange.7">
                    {t('publish.teams.propagation_note')}
                  </Text>
                </Stack>
              )}

              <Group justify="flex-end" mt="lg">
                <Button type="submit" loading={submitting}>
                  {t('publish.teams.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      {/* Twilio 配置弹窗（电话受付，只存秘钥）*/}
      <Modal
        opened={twilioModalOpened}
        onClose={closeTwilioModal}
        title={
          <Group justify="space-between" w="100%">
            <Text size="lg">
              {isEditingTwilio
                ? `${t('publish.twilio.modal_title')} - ${t('publish.actions.edit')}`
                : t('publish.twilio.modal_title')}
            </Text>
            <ActionIcon variant="subtle" color="gray" onClick={closeTwilioModal}>
              <IconX size="1rem" />
            </ActionIcon>
          </Group>
        }
        size="md"
        centered
        withCloseButton={false}
        styles={{ title: { width: '100%' } }}
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.6">
            {t('publish.twilio.modal_description')}
          </Text>

          <form onSubmit={twilioForm.onSubmit(handleCreateTwilioIntegration)}>
            <Stack gap="lg">
              {/* Voice webhook URL：配置前即可复制，粘到 Twilio 控制台对应号码的 Voice webhook */}
              <Stack gap="xs">
                <Text size="sm">{t('publish.twilio.webhook_label')}</Text>
                <Group gap="xs" align="center">
                  <Text
                    size="sm"
                    c="gray.7"
                    style={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f8f9fa',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef',
                      flex: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {generateTwilioWebhookUrl()}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    onClick={() => {
                      const ok = copyToClipboard(generateTwilioWebhookUrl());
                      setCopied(!!ok);
                    }}
                  >
                    {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                  </ActionIcon>
                </Group>
                <Text size="xs" c="gray.5">
                  {t('publish.twilio.webhook_hint')}
                </Text>
              </Stack>

              <Stack gap="md">
                <TextInput
                  label={
                    <Group gap="xs">
                      <Text size="sm">{t('publish.twilio.account_sid')}</Text>
                      <Text c="red" size="sm">
                        *
                      </Text>
                    </Group>
                  }
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  description={t('publish.twilio.account_sid_description')}
                  {...twilioForm.getInputProps('account_sid')}
                  styles={{ input: { fontSize: '14px' } }}
                />

                <TextInput
                  label={
                    <Group gap="xs">
                      <Text size="sm">{t('publish.twilio.auth_token')}</Text>
                      <Text c="red" size="sm">
                        *
                      </Text>
                    </Group>
                  }
                  type="password"
                  description={t('publish.twilio.auth_token_description')}
                  {...twilioForm.getInputProps('auth_token')}
                  styles={{ input: { fontSize: '14px' } }}
                />

                <TextInput
                  label={
                    <Group gap="xs">
                      <Text size="sm">{t('publish.twilio.phone_number')}</Text>
                      <Text c="red" size="sm">
                        *
                      </Text>
                    </Group>
                  }
                  placeholder="+815012345678"
                  description={t('publish.twilio.phone_number_description')}
                  {...twilioForm.getInputProps('phone_number')}
                  styles={{ input: { fontSize: '14px' } }}
                />

                {/* friendly_name（显示名称）暂不开放：后端字段保留，待着信ログ/多号码场景再启用 */}
              </Stack>

              <Text size="xs" c="gray.5">
                {t('publish.twilio.behavior_hint')}
              </Text>

              <Group justify="flex-end" mt="lg">
                <Button type="submit" loading={submitting}>
                  {t('publish.twilio.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      {/* Zendesk 回复环配置弹窗 */}
      <Modal
        opened={zendeskModalOpened}
        onClose={closeZendeskModal}
        title={
          <Group justify="space-between" w="100%">
            <Text size="lg">
              {isEditingZendesk
                ? `${t('publish.zendesk.modal_title')} - ${t('publish.actions.edit')}`
                : t('publish.zendesk.modal_title')}
            </Text>
            <ActionIcon variant="subtle" color="gray" onClick={closeZendeskModal}>
              <IconX size="1rem" />
            </ActionIcon>
          </Group>
        }
        size="lg"
        centered
        withCloseButton={false}
        styles={{ title: { width: '100%' } }}
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.6">
            {t('publish.zendesk.modal_description')}
          </Text>
          <form onSubmit={zendeskForm.onSubmit(handleCreateZendeskIntegration)}>
            <Stack gap="md">
              <Text size="sm" fw={600}>
                {t('publish.zendesk.connection_step')}
              </Text>
              <Select
                label={t('publish.zendesk.connection_mode')}
                allowDeselect={false}
                data={[
                  { value: 'existing', label: t('publish.zendesk.use_existing_connection') },
                  { value: 'new', label: t('publish.zendesk.new_connection') },
                  { value: 'oauth', label: t('zendesk_oauth.new_connection') },
                ]}
                {...zendeskForm.getInputProps('connection_mode')}
              />
              {zendeskForm.values.connection_mode === 'existing' ? (
                <>
                  <Select
                    label={t('publish.zendesk.connection')}
                    placeholder={t('publish.zendesk.connection_placeholder')}
                    nothingFoundMessage={t('publish.zendesk.no_connections')}
                    data={zendeskConnections.map((c) => ({
                      value: c.id,
                      label: `${c.subdomain}.zendesk.com（${c.friendly_name || c.admin_email}）${
                        c.used_by?.length
                          ? `　${t('publish.zendesk.shared.in_use_by', { count: c.used_by.length })}`
                          : ''
                      }`,
                    }))}
                    {...zendeskForm.getInputProps('connection_id')}
                  />
                  <ZendeskOAuth
                    key={zendeskForm.values.connection_id}
                    connectionId={zendeskForm.values.connection_id || undefined}
                    subdomain={
                      zendeskConnections.find((c) => c.id === zendeskForm.values.connection_id)?.subdomain || ''
                    }
                    authentication={
                      zendeskConnections.find((c) => c.id === zendeskForm.values.connection_id)?.authentication
                    }
                    onAuthorized={() => {
                      if (botId)
                        return getZendeskConnections(botId).then(({ data }) =>
                          setZendeskConnections(data.connections || [])
                        );
                    }}
                    onCredentialsSaved={() => {
                      if (botId)
                        return getZendeskConnections(botId).then(({ data }) =>
                          setZendeskConnections(data.connections || [])
                        );
                    }}
                  />
                </>
              ) : zendeskForm.values.connection_mode === 'oauth' ? (
                <>
                  <TextInput label={t('publish.zendesk.subdomain')} {...zendeskForm.getInputProps('subdomain')} />
                  <TextInput
                    label={t('publish.zendesk.friendly_name')}
                    {...zendeskForm.getInputProps('friendly_name')}
                  />
                  <ZendeskOAuth
                    subdomain={normalizeZendeskSubdomain(zendeskForm.values.subdomain)}
                    friendlyName={zendeskForm.values.friendly_name}
                    onAuthorized={async (id) => {
                      if (botId) {
                        const { data } = await getZendeskConnections(botId);
                        setZendeskConnections(data.connections || []);
                      }
                      zendeskForm.setFieldValue('connection_id', id);
                      zendeskForm.setFieldValue('connection_mode', 'existing');
                    }}
                  />
                </>
              ) : (
                <Stack gap="md">
                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.zendesk.subdomain')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    placeholder="your-subdomain"
                    description={t('publish.zendesk.subdomain_description')}
                    {...zendeskForm.getInputProps('subdomain')}
                  />
                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.zendesk.admin_email')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    placeholder="agent@example.com"
                    description={t('publish.zendesk.admin_email_description')}
                    {...zendeskForm.getInputProps('admin_email')}
                  />
                  <TextInput
                    type="password"
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.zendesk.api_token')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.zendesk.api_token_description')}
                    placeholder={isEditingZendesk ? t('publish.zendesk.api_token_edit_placeholder') : ''}
                    {...zendeskForm.getInputProps('api_token')}
                  />
                  <TextInput
                    label={t('publish.zendesk.friendly_name')}
                    {...zendeskForm.getInputProps('friendly_name')}
                  />
                </Stack>
              )}

              <Text size="sm" fw={600} mt="sm">
                {t('publish.zendesk.behavior_step')}
              </Text>
              {zendeskForms ? (
                <Stack gap={4}>
                  <MultiSelect
                    label={t('publish.zendesk.target_forms')}
                    description={t('publish.zendesk.target_forms_description')}
                    placeholder={t('publish.zendesk.target_forms_placeholder')}
                    searchable
                    clearable
                    data={zendeskForms.forms.map((f) => ({
                      value: String(f.id),
                      label: `${f.name}${f.active ? '' : `（${t('publish.zendesk.form_inactive')}）`}　${t(
                        'publish.zendesk.form_recent',
                        { count: f.recent_tickets }
                      )}`,
                    }))}
                    value={parseZendeskFormIds(zendeskForm.values.target_form_ids).map(String)}
                    onChange={(ids) => zendeskForm.setFieldValue('target_form_ids', ids.join(', '))}
                    error={zendeskForm.errors.target_form_ids}
                  />
                  {zendeskCoverage && (
                    <Text size="xs" c={zendeskCoverage.all ? 'yellow.8' : 'gray.7'}>
                      {zendeskCoverage.all
                        ? t('publish.zendesk.coverage_all', {
                            total: zendeskCoverage.total,
                            days: zendeskForms.days,
                          })
                        : t('publish.zendesk.coverage', {
                            covered: zendeskCoverage.covered,
                            total: zendeskCoverage.total,
                            days: zendeskForms.days,
                          })}
                      {zendeskCoverage.unknown > 0 && !zendeskCoverage.all
                        ? `　${t('publish.zendesk.coverage_unknown', { count: zendeskCoverage.unknown })}`
                        : ''}
                    </Text>
                  )}
                  {!!zendeskForms.unknown_selected?.length && (
                    <Text size="xs" c="red.6">
                      {t('publish.zendesk.forms_unknown_selected', { ids: zendeskForms.unknown_selected.join(', ') })}
                    </Text>
                  )}
                </Stack>
              ) : (
                <TextInput
                  label={t('publish.zendesk.target_form_ids')}
                  description={
                    zendeskFormsLoading
                      ? t('publish.zendesk.forms_loading')
                      : zendeskForm.values.connection_mode === 'existing'
                      ? t('publish.zendesk.forms_unavailable')
                      : t('publish.zendesk.forms_after_save')
                  }
                  placeholder="360001234567, 360007654321"
                  rightSection={zendeskFormsLoading ? <Loader size="xs" /> : undefined}
                  {...zendeskForm.getInputProps('target_form_ids')}
                />
              )}
              {zendeskSharers.length > 0 && (
                <Alert
                  color={zendeskSharers.some((s) => s.form_overlap) ? 'yellow' : 'blue'}
                  variant="light"
                  title={t('publish.zendesk.shared.title')}
                >
                  <Stack gap={4}>
                    {zendeskSharers.map((s) => (
                      <Text key={s.robot_id} size="xs">
                        ・{s.robot_name || s.robot_id}：
                        {s.target_form_ids?.length
                          ? t('publish.zendesk.shared.forms', { forms: s.target_form_ids.join(', ') })
                          : t('publish.zendesk.shared.all_forms')}
                        {s.form_overlap ? `　${t('publish.zendesk.shared.overlap_mark')}` : ''}
                      </Text>
                    ))}
                    <Text size="xs" c={zendeskSharers.some((s) => s.form_overlap) ? 'yellow.9' : 'gray.7'}>
                      {zendeskSharers.some((s) => s.form_overlap)
                        ? t('publish.zendesk.shared.overlap_desc')
                        : t('publish.zendesk.shared.no_overlap_desc')}
                    </Text>
                  </Stack>
                </Alert>
              )}
              <Textarea
                label={t('publish.zendesk.signature_patterns')}
                description={t('publish.zendesk.signature_patterns_description')}
                autosize
                minRows={2}
                {...zendeskForm.getInputProps('signature_patterns')}
              />
              <Stack
                gap="xs"
                p="sm"
                style={{ border: '1px dashed var(--mantine-color-gray-3)', borderRadius: 'var(--mantine-radius-md)' }}
              >
                <Text size="xs" fw={600}>
                  {t('publish.zendesk.signature_test.title')}
                </Text>
                <Textarea
                  size="xs"
                  autosize
                  minRows={3}
                  placeholder={t('publish.zendesk.signature_test.placeholder')}
                  value={zendeskSigSample}
                  onChange={(e) => setZendeskSigSample(e.currentTarget.value)}
                />
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    loading={zendeskTesting}
                    disabled={!zendeskSigSample.trim()}
                    onClick={handleZendeskSignatureTest}
                  >
                    {t('publish.zendesk.signature_test.run')}
                  </Button>
                  {zendeskSigResult && (
                    <Text size="xs" c={zendeskSigResult.removed ? 'teal.7' : 'gray.6'}>
                      {zendeskSigResult.removed
                        ? t('publish.zendesk.signature_test.matched', {
                            pattern: zendeskSigResult.matched_pattern || '—',
                          })
                        : t('publish.zendesk.signature_test.no_match')}
                    </Text>
                  )}
                </Group>
                {!!zendeskSigResult?.invalid_patterns?.length && (
                  <Text size="xs" c="red.6">
                    {t('publish.zendesk.signature_test.invalid', {
                      patterns: zendeskSigResult.invalid_patterns.join(' / '),
                    })}
                  </Text>
                )}
                {zendeskSigResult && (
                  <Group grow align="flex-start" gap="xs">
                    <Stack gap={2}>
                      <Text size="xs" c="gray.6">
                        {t('publish.zendesk.signature_test.kept')}
                      </Text>
                      <Text
                        size="xs"
                        style={{ whiteSpace: 'pre-wrap', background: '#F1FBF7', padding: 8, borderRadius: 6 }}
                      >
                        {zendeskSigResult.cleaned || '—'}
                      </Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="gray.6">
                        {t('publish.zendesk.signature_test.removed')}
                      </Text>
                      <Text
                        size="xs"
                        c="red.7"
                        style={{ whiteSpace: 'pre-wrap', background: '#FFF5F5', padding: 8, borderRadius: 6 }}
                      >
                        {zendeskSigResult.removed || '—'}
                      </Text>
                    </Stack>
                  </Group>
                )}
              </Stack>
              <Switch
                label={t('publish.zendesk.draft_note_enabled')}
                description={t('publish.zendesk.draft_note_enabled_description')}
                {...zendeskForm.getInputProps('draft_note_enabled', { type: 'checkbox' })}
              />
              <Switch
                label={t('publish.zendesk.show_sources_in_note')}
                {...zendeskForm.getInputProps('show_sources_in_note', { type: 'checkbox' })}
              />

              <Stack gap="xs">
                <Text size="sm">{t('publish.zendesk.webhook_label')}</Text>
                <Group gap="xs" align="center">
                  <Text
                    size="sm"
                    c="gray.7"
                    style={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f8f9fa',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef',
                      flex: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {zendeskWebhookUrl}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    onClick={() => {
                      const ok = copyToClipboard(zendeskWebhookUrl);
                      setCopied(!!ok);
                    }}
                  >
                    {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                  </ActionIcon>
                </Group>
                <Text size="xs" c="gray.6">
                  {t('publish.zendesk.webhook_hint')}
                </Text>
              </Stack>

              {existingZendesk && (
                <Stack
                  gap="sm"
                  p="md"
                  style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 'var(--mantine-radius-md)' }}
                >
                  {existingZendesk.health ? (
                    <Stack gap={4}>
                      <Text size="xs" fw={600}>
                        {t('publish.zendesk.health.title')}
                      </Text>
                      {(
                        [
                          [
                            'connection',
                            existingZendesk.health.connection.status === 'active',
                            existingZendesk.health.connection.last_tested_at,
                            existingZendesk.health.connection.last_error,
                          ],
                          [
                            'webhook',
                            existingZendesk.health.webhook.registered,
                            null,
                            existingZendesk.health.webhook.last_error,
                          ],
                          [
                            'receive',
                            !!existingZendesk.health.receive.last_event_at &&
                              !existingZendesk.health.receive.last_error,
                            existingZendesk.health.receive.last_event_at,
                            existingZendesk.health.receive.last_error,
                          ],
                          [
                            'post',
                            existingZendesk.health.post.dry_run
                              ? null
                              : !!existingZendesk.health.post.last_posted_at && !existingZendesk.health.post.last_error,
                            existingZendesk.health.post.last_posted_at,
                            existingZendesk.health.post.last_error,
                          ],
                        ] as [string, boolean | null, string | null | undefined, string | null | undefined][]
                      ).map(([key, ok, at, err]) => (
                        <Group key={key} gap={6} wrap="nowrap" align="flex-start">
                          <Text size="xs" c="gray.6" w={120} style={{ flexShrink: 0 }}>
                            {t(`publish.zendesk.health.${key}`)}
                          </Text>
                          <Badge
                            size="xs"
                            variant="light"
                            color={ok === null ? 'grape' : ok ? 'green' : at || err ? 'red' : 'gray'}
                            style={{ flexShrink: 0 }}
                          >
                            {ok === null
                              ? t('publish.zendesk.health.dry_run')
                              : ok
                              ? t('publish.zendesk.health.ok')
                              : at || err
                              ? t('publish.zendesk.health.error')
                              : t('publish.zendesk.health.none')}
                          </Badge>
                          <Text size="xs" c={err ? 'red.6' : 'gray.6'} style={{ flex: 1, wordBreak: 'break-word' }}>
                            {err ||
                              (at
                                ? t('publish.zendesk.health.last_at', { time: new Date(at).toLocaleString() })
                                : key === 'webhook'
                                ? existingZendesk.health!.webhook.id || ''
                                : t('publish.zendesk.health.never'))}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  ) : (
                    <Group gap="xs" align="center">
                      <Text size="sm">{t('publish.zendesk.webhook_status')}</Text>
                      <Badge color={existingZendesk.webhook_registered ? 'green' : 'red'} variant="light" size="sm">
                        {existingZendesk.webhook_registered
                          ? t('publish.zendesk.webhook_registered')
                          : t('publish.zendesk.webhook_not_registered')}
                      </Badge>
                      {existingZendesk.last_error && (
                        <Text size="xs" c="red">
                          {existingZendesk.last_error}
                        </Text>
                      )}
                    </Group>
                  )}
                  {existingZendesk.purposes && (
                    <Stack gap={4} style={{ borderTop: '1px solid var(--mantine-color-gray-2)', paddingTop: 8 }}>
                      <Text size="xs" fw={600}>
                        {t('publish.zendesk.purposes.title')}
                      </Text>
                      <Group gap={6} wrap="nowrap" align="center">
                        <Text size="xs" c="gray.6" w={120} style={{ flexShrink: 0 }}>
                          {t('publish.zendesk.purposes.draft')}
                        </Text>
                        <Badge
                          size="xs"
                          variant="light"
                          color={
                            existingZendesk.purposes.draft.paused
                              ? 'yellow'
                              : existingZendesk.purposes.draft.dry_run
                              ? 'grape'
                              : 'green'
                          }
                        >
                          {existingZendesk.purposes.draft.paused
                            ? t('publish.zendesk.purposes.paused')
                            : existingZendesk.purposes.draft.dry_run
                            ? t('publish.zendesk.purposes.dry_run')
                            : t('publish.zendesk.purposes.on')}
                        </Badge>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color={existingZendesk.purposes.draft.paused ? 'green' : 'yellow'}
                          loading={zendeskPausing}
                          onClick={handleToggleZendeskPause}
                        >
                          {existingZendesk.purposes.draft.paused
                            ? t('publish.zendesk.purposes.resume')
                            : t('publish.zendesk.purposes.pause')}
                        </Button>
                      </Group>
                      <Group gap={6} wrap="nowrap" align="center">
                        <Text size="xs" c="gray.6" w={120} style={{ flexShrink: 0 }}>
                          {t('publish.zendesk.purposes.learn_edits')}
                        </Text>
                        <Badge
                          size="xs"
                          variant="light"
                          color={existingZendesk.purposes.learn_edits?.enabled ? 'green' : 'gray'}
                        >
                          {existingZendesk.purposes.learn_edits?.enabled
                            ? t('publish.zendesk.purposes.on')
                            : t('publish.zendesk.purposes.off')}
                        </Badge>
                        {existingZendesk.purposes.learn_edits?.dataset_id && (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            component="a"
                            href={`/bot/${botId}/datasets/${existingZendesk.purposes.learn_edits.dataset_id}/data-source/zendesk?tab=distill`}
                          >
                            {t('publish.zendesk.purposes.open_loop')}
                          </Button>
                        )}
                      </Group>
                      <Group gap={6} wrap="nowrap" align="flex-start">
                        <Text size="xs" c="gray.6" w={120} style={{ flexShrink: 0 }}>
                          {t('publish.zendesk.purposes.import')}
                        </Text>
                        <Text size="xs" style={{ flex: 1 }}>
                          {existingZendesk.purposes.import.length
                            ? existingZendesk.purposes.import.map((s) => s.dataset_name || s.dataset_id).join('、')
                            : t('publish.zendesk.purposes.import_none')}
                        </Text>
                      </Group>
                      <Text size="xs" c="gray.5">
                        {t('publish.zendesk.purposes.hint')}
                      </Text>
                    </Stack>
                  )}
                  {!!existingZendesk.shared_with?.length && (
                    <Alert color={existingZendesk.form_overlap ? 'yellow' : 'blue'} variant="light" p="xs">
                      <Text size="xs">
                        {t(
                          existingZendesk.form_overlap
                            ? 'publish.zendesk.shared.saved_overlap'
                            : 'publish.zendesk.shared.saved_no_overlap',
                          {
                            bots: existingZendesk.shared_with
                              .map((s: ZendeskConnectionSharer) => s.robot_name || s.robot_id)
                              .join('、'),
                          }
                        )}
                      </Text>
                    </Alert>
                  )}
                  {existingZendesk.effective && (
                    <Stack gap={4} style={{ borderTop: '1px solid var(--mantine-color-gray-2)', paddingTop: 8 }}>
                      <Text size="xs" fw={600}>
                        {t('publish.zendesk.effective.title')}
                      </Text>
                      {(
                        [
                          [
                            'model',
                            existingZendesk.effective.model.value || t('publish.zendesk.effective.default_model'),
                            existingZendesk.effective.model.source,
                          ],
                          [
                            'datasets',
                            existingZendesk.effective.datasets.map((d) => d.name).join('、') ||
                              t('publish.zendesk.effective.none'),
                            'bot',
                          ],
                          [
                            'no_answer',
                            existingZendesk.effective.no_answer_behavior.require_grounding
                              ? t('publish.zendesk.effective.no_answer_grounded')
                              : t('publish.zendesk.effective.no_answer_judge'),
                            existingZendesk.effective.no_answer_behavior.source,
                          ],
                          [
                            'forms',
                            existingZendesk.effective.target_forms.all_forms
                              ? t('publish.zendesk.effective.all_forms')
                              : existingZendesk.effective.target_forms.value.join(', '),
                            'channel',
                          ],
                          [
                            'note',
                            t(`publish.zendesk.effective.note_${existingZendesk.effective.note_target.value}`),
                            'channel',
                          ],
                          [
                            'review',
                            t(`knowledge_loop.publish_mode.${existingZendesk.effective.review_policy.publish_mode}`),
                            existingZendesk.effective.review_policy.source,
                          ],
                        ] as [string, string, string][]
                      ).map(([key, value, source]) => (
                        <Group key={key} gap={6} wrap="nowrap" align="flex-start">
                          <Text size="xs" c="gray.6" w={120} style={{ flexShrink: 0 }}>
                            {t(`publish.zendesk.effective.${key}`)}
                          </Text>
                          <Text size="xs" style={{ flex: 1, wordBreak: 'break-word' }}>
                            {value}
                          </Text>
                          <Badge size="xs" variant="outline" color="gray" style={{ flexShrink: 0 }}>
                            {t(`publish.zendesk.effective.source_${source}`, source)}
                          </Badge>
                        </Group>
                      ))}
                    </Stack>
                  )}
                  <Group gap="xs">
                    <Button variant="outline" size="xs" loading={zendeskTesting} onClick={handleTestZendesk}>
                      {t('publish.zendesk.test_connection')}
                    </Button>
                    {!existingZendesk.webhook_registered && (
                      <Button
                        variant="outline"
                        size="xs"
                        loading={zendeskTesting}
                        onClick={handleRegisterZendeskWebhook}
                      >
                        {t('publish.zendesk.register_webhook')}
                      </Button>
                    )}
                  </Group>
                  {!existingZendesk.webhook_registered && (
                    <Group gap="xs" align="flex-end">
                      <TextInput
                        style={{ flex: 1 }}
                        size="xs"
                        label={t('publish.zendesk.signing_secret')}
                        description={t('publish.zendesk.signing_secret_description')}
                        placeholder={t('publish.zendesk.signing_secret_placeholder')}
                        value={zendeskSecretInput}
                        onChange={(e) => setZendeskSecretInput(e.currentTarget.value)}
                      />
                      <Button
                        size="xs"
                        variant="light"
                        loading={zendeskTesting}
                        disabled={!zendeskSecretInput.trim()}
                        onClick={handleSaveZendeskSecret}
                      >
                        {t('publish.zendesk.save_secret')}
                      </Button>
                    </Group>
                  )}
                </Stack>
              )}

              <Group justify="flex-end" mt="lg">
                <Button type="submit" loading={submitting}>
                  {t('publish.zendesk.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      {/* Lark 通知配置弹窗 */}
      <Modal
        opened={larkModalOpened}
        onClose={closeLarkModal}
        title={
          <Group justify="space-between" w="100%">
            <Text fw={600}>
              {isEditingLark
                ? `${t('publish.lark.modal_title')} - ${t('publish.actions.edit')}`
                : t('publish.lark.modal_title')}
            </Text>
          </Group>
        }
        size="lg"
        centered
        styles={{ title: { width: '100%' } }}
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.6">
            {t('publish.lark.modal_description')}
          </Text>

          <form onSubmit={larkForm.onSubmit(handleCreateLarkIntegration)}>
            <Stack gap="lg">
              {/* 分组①：通知 webhook（任意） */}
              <Stack gap="md">
                <Text fz={12} fw={700} c="gray.6">
                  {t('publish.lark.group_notify')}
                </Text>
                <TextInput
                  label={<Text size="sm">{t('publish.lark.webhook_url')}</Text>}
                  placeholder={
                    isEditingLark ? t('publish.lark.keep_hint') : 'https://open.larksuite.com/open-apis/bot/v2/hook/...'
                  }
                  description={t('publish.lark.webhook_url_description')}
                  {...larkForm.getInputProps('webhook_url')}
                  styles={{ input: { fontSize: '14px' } }}
                />

                <TextInput
                  label={<Text size="sm">{t('publish.lark.secret')}</Text>}
                  type="password"
                  placeholder={isEditingLark ? t('publish.lark.keep_hint') : undefined}
                  description={t('publish.lark.secret_description')}
                  {...larkForm.getInputProps('secret')}
                  styles={{ input: { fontSize: '14px' } }}
                />
              </Stack>

              {/* 分组②：日历连携应用凭据（任意，日程查询/预约用） */}
              <Stack gap="md">
                <Text fz={12} fw={700} c="gray.6">
                  {t('publish.lark.group_calendar')}
                </Text>
                <Text fz={12} c="gray.5" mt={-8}>
                  {t('publish.lark.group_calendar_description')}
                </Text>
                <TextInput
                  label={<Text size="sm">{t('publish.lark.app_id')}</Text>}
                  placeholder={isEditingLark ? t('publish.lark.keep_hint') : 'cli_xxxxxxxxxxxx'}
                  description={t('publish.lark.app_id_description')}
                  {...larkForm.getInputProps('app_id')}
                  styles={{ input: { fontSize: '14px' } }}
                />
                <TextInput
                  label={<Text size="sm">{t('publish.lark.app_secret')}</Text>}
                  type="password"
                  placeholder={isEditingLark ? t('publish.lark.keep_hint') : undefined}
                  description={t('publish.lark.app_secret_description')}
                  {...larkForm.getInputProps('app_secret')}
                  styles={{ input: { fontSize: '14px' } }}
                />
              </Stack>

              <TextInput
                label={<Text size="sm">{t('publish.lark.friendly_name')}</Text>}
                description={t('publish.lark.friendly_name_description')}
                {...larkForm.getInputProps('friendly_name')}
                styles={{ input: { fontSize: '14px' } }}
              />

              <Text size="xs" c="gray.5">
                {t('publish.lark.usage_hint')}
              </Text>

              <Group justify="flex-end" mt="lg">
                <Button type="submit" loading={submitting}>
                  {t('publish.lark.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      {/* WowTalk 配置弹窗 */}
      <Modal
        opened={wowtalkModalOpened}
        onClose={closeWowTalkModal}
        title={
          <Group justify="space-between" w="100%">
            <Text size="lg">
              {isEditingWowTalk
                ? `${t('publish.wowtalk.modal_title')} - ${t('publish.actions.edit')}`
                : t('publish.wowtalk.modal_title')}
            </Text>
            <ActionIcon variant="subtle" color="gray" onClick={closeWowTalkModal}>
              <IconX size="1rem" />
            </ActionIcon>
          </Group>
        }
        size="md"
        centered
        withCloseButton={false}
        styles={{
          title: { width: '100%' },
        }}
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.6">
            {t('publish.wowtalk.modal_description')}
          </Text>

          {isEditingWowTalk && (
            <Text
              size="sm"
              c="orange.6"
              style={{ backgroundColor: '#fff4e6', padding: '8px 12px', borderRadius: '4px' }}
            >
              {t('publish.wowtalk.edit_mode_notice')}
            </Text>
          )}

          <form onSubmit={wowtalkForm.onSubmit(handleCreateWowTalkIntegration)}>
            <Stack gap="lg">
              {/* Step 1: Callback URL */}
              <Stack gap="sm">
                <Text size="sm">{t('publish.wowtalk.callback_step')}</Text>

                <Stack gap="xs">
                  <Text size="sm">{t('publish.wowtalk.callback_label')}</Text>
                  <Group gap="xs" align="center">
                    <Text
                      size="sm"
                      c="gray.7"
                      style={{
                        fontFamily: 'monospace',
                        backgroundColor: '#f8f9fa',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef',
                        flex: 1,
                        wordBreak: 'break-all',
                      }}
                    >
                      {generateCallbackUrl()}
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => {
                        const copied = copyToClipboard(generateCallbackUrl());
                        setCopied(!!copied);
                      }}
                    >
                      {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                    </ActionIcon>
                  </Group>
                </Stack>
              </Stack>

              {/* Step 2: WowTalk Credentials */}
              <Stack gap="sm">
                <Text size="sm">{t('publish.wowtalk.credentials_step')}</Text>

                <Stack gap="md">
                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.wowtalk.company_id')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.wowtalk.company_id_description')}
                    {...wowtalkForm.getInputProps('company_id')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.wowtalk.from_account')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.wowtalk.from_account_description')}
                    {...wowtalkForm.getInputProps('from_account')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    type="password"
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.wowtalk.from_password')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.wowtalk.from_password_description')}
                    placeholder={
                      isEditingWowTalk
                        ? t('publish.wowtalk.password_edit_placeholder')
                        : t('publish.wowtalk.from_password_placeholder')
                    }
                    {...wowtalkForm.getInputProps('from_password')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.wowtalk.api_base_url')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.wowtalk.api_base_url_description')}
                    placeholder={t('publish.wowtalk.api_base_url_placeholder')}
                    {...wowtalkForm.getInputProps('api_base_url')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    type="password"
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.wowtalk.api_key')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.wowtalk.api_key_description')}
                    placeholder={
                      isEditingWowTalk
                        ? t('publish.wowtalk.api_key_edit_placeholder')
                        : t('publish.wowtalk.api_key_placeholder')
                    }
                    {...wowtalkForm.getInputProps('api_key')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    type="password"
                    label={<Text size="sm">{t('publish.wowtalk.callback_token')}</Text>}
                    description={t('publish.wowtalk.callback_token_description')}
                    placeholder={isEditingWowTalk ? t('publish.wowtalk.callback_token_edit_placeholder') : ''}
                    {...wowtalkForm.getInputProps('callback_token')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />
                </Stack>
              </Stack>

              <Group justify="flex-end" mt="lg">
                <Button type="submit" loading={submitting}>
                  {isEditingWowTalk ? t('publish.actions.update') : t('publish.wowtalk.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      {/* LINE WORKS 配置弹窗 */}
      <Modal
        opened={lineworksModalOpened}
        onClose={closeLineWorksModal}
        title={
          <Group justify="space-between" w="100%">
            <Text size="lg">
              {isEditingLineWorks
                ? `${t('publish.lineworks.modal_title')} - ${t('publish.actions.edit')}`
                : t('publish.lineworks.modal_title')}
            </Text>
            <ActionIcon variant="subtle" color="gray" onClick={closeLineWorksModal}>
              <IconX size="1rem" />
            </ActionIcon>
          </Group>
        }
        size="md"
        centered
        withCloseButton={false}
        styles={{
          title: { width: '100%' },
        }}
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.6">
            {t('publish.lineworks.modal_description')}
          </Text>

          {isEditingLineWorks && (
            <Text
              size="sm"
              c="orange.6"
              style={{ backgroundColor: '#fff4e6', padding: '8px 12px', borderRadius: '4px' }}
            >
              {t('publish.lineworks.edit_mode_notice')}
            </Text>
          )}

          <form onSubmit={lineworksForm.onSubmit(handleCreateLineWorksIntegration)}>
            <Stack gap="lg">
              {/* Step 1: Webhook URL */}
              <Stack gap="sm">
                <Group gap="xs">
                  <Box
                    w={20}
                    h={20}
                    bg="blue.6"
                    c="white"
                    style={{
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    1
                  </Box>
                  <Text size="sm">{t('publish.lineworks.webhook_step')}</Text>
                </Group>

                <Stack gap="xs">
                  <Text size="sm">{t('publish.lineworks.webhook_label')}</Text>
                  <Group gap="xs" align="center">
                    <Text
                      size="sm"
                      c="gray.7"
                      style={{
                        fontFamily: 'monospace',
                        backgroundColor: '#f8f9fa',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef',
                        flex: 1,
                        wordBreak: 'break-all',
                      }}
                    >
                      {generateLineWorksWebhookUrl()}
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => {
                        const copied = copyToClipboard(generateLineWorksWebhookUrl());
                        setCopied(!!copied);
                      }}
                    >
                      {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                    </ActionIcon>
                  </Group>
                </Stack>
              </Stack>

              {/* Step 2: LINE WORKS Credentials */}
              <Stack gap="sm">
                <Group gap="xs">
                  <Box
                    w={20}
                    h={20}
                    bg="blue.6"
                    c="white"
                    style={{
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    2
                  </Box>
                  <Text size="sm">{t('publish.lineworks.credentials_step')}</Text>
                </Group>

                <Stack gap="md">
                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.lineworks.client_id')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.lineworks.client_id_description')}
                    {...lineworksForm.getInputProps('client_id')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    type="password"
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.lineworks.client_secret')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.lineworks.client_secret_description')}
                    placeholder={isEditingLineWorks ? t('publish.lineworks.client_secret_edit_placeholder') : ''}
                    {...lineworksForm.getInputProps('client_secret')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.lineworks.service_account')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.lineworks.service_account_description')}
                    {...lineworksForm.getInputProps('service_account')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  <TextInput
                    label={
                      <Group gap="xs">
                        <Text size="sm">{t('publish.lineworks.bot_id')}</Text>
                        <Text c="red" size="sm">
                          *
                        </Text>
                      </Group>
                    }
                    description={t('publish.lineworks.bot_id_description')}
                    {...lineworksForm.getInputProps('bot_id')}
                    styles={{
                      input: {
                        fontSize: '14px',
                      },
                    }}
                  />

                  {/* Private Key - 文件上传 */}
                  <Box>
                    <Group gap="xs" mb={4}>
                      <Text size="sm">{t('publish.lineworks.private_key')}</Text>
                      <Text c="red" size="sm">
                        *
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" mb="xs">
                      {t('publish.lineworks.private_key_description')}
                    </Text>

                    <Group gap="sm" mb="xs">
                      <FileButton resetRef={resetFileRef} onChange={handlePrivateKeyFileUpload} accept=".pem,.key,.txt">
                        {(props) => (
                          <Button variant="light" size="sm" leftSection={<IconUpload size="1rem" />} {...props}>
                            {t('publish.lineworks.private_key_upload_button')}
                          </Button>
                        )}
                      </FileButton>
                      {privateKeyFileName && (
                        <Text size="sm" c="green.6">
                          {privateKeyFileName}
                        </Text>
                      )}
                    </Group>

                    {lineworksForm.errors.private_key && (
                      <Text size="xs" c="red">
                        {lineworksForm.errors.private_key}
                      </Text>
                    )}

                    {lineworksForm.values.private_key && (
                      <Text size="xs" c="green.6">
                        {t('publish.lineworks.private_key_loaded')}
                      </Text>
                    )}
                  </Box>
                </Stack>
              </Stack>

              <Group justify="flex-end" mt="lg">
                <Button type="submit" loading={submitting}>
                  {isEditingLineWorks ? t('publish.actions.update') : t('publish.lineworks.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      {/* PORTERS 配置弹窗 */}
      <Modal
        opened={portersModalOpened}
        onClose={closePortersModal}
        title={
          <Group justify="space-between" w="100%">
            <Text size="lg">{t('publish.porters.modal_title', 'PORTERS 連携設定')}</Text>
            <ActionIcon variant="subtle" color="gray" onClick={closePortersModal}>
              <IconX size="1rem" />
            </ActionIcon>
          </Group>
        }
        size="lg"
        centered
        withCloseButton={false}
        styles={{ title: { width: '100%' } }}
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.6">
            {t(
              'publish.porters.modal_description',
              'PORTERS HRBC からの社員情報をチャットの個別化に利用します。下記の情報は PORTERS から発行された接続情報メールに記載されています。'
            )}
          </Text>

          <form onSubmit={portersForm.onSubmit(handleSavePortersIntegration)}>
            <Stack gap="md">
              <TextInput
                label={t('publish.porters.company_id_label', 'PORTERS 会社 ID')}
                placeholder={t('publish.porters.company_id_placeholder', '例: brexa_next')}
                required
                {...portersForm.getInputProps('porters_company_id')}
              />
              <TextInput
                label={t('publish.porters.app_id_label', 'App ID')}
                placeholder={t('publish.porters.app_id_placeholder', '32桁の16進数文字列')}
                required
                {...portersForm.getInputProps('app_id')}
              />
              <TextInput
                label={t('publish.porters.secret_label', 'Secret')}
                placeholder={t('publish.porters.secret_placeholder', '64桁の16進数文字列')}
                required
                description={t(
                  'publish.porters.secret_description',
                  '※排查方便, Secret 以明文存储和回显, 仅 bot owner 可见'
                )}
                {...portersForm.getInputProps('secret')}
              />
              <TextInput
                label={t('publish.porters.api_host_label', 'API Host')}
                placeholder="api-hrbc-jp.porterscloud.com"
                required
                {...portersForm.getInputProps('api_host')}
              />
              <TextInput
                label={t('publish.porters.scopes_label', 'Scopes')}
                placeholder="partition_r,user_r"
                description={t('publish.porters.scopes_description', '逗号分隔; 必须与 PORTERS 申请时一致')}
                {...portersForm.getInputProps('scopes')}
              />
              <TextInput
                label={t('publish.porters.line_field_label', 'LINE userId 字段 alias')}
                placeholder="User.P_LINE_UserID"
                description={t(
                  'publish.porters.line_field_description',
                  '客户在 PORTERS User 资源上加的自定义字段 alias (可空)'
                )}
                {...portersForm.getInputProps('line_userid_field_alias')}
              />

              {/* 测试结果展示 */}
              {portersTestResult && (
                <Box>
                  <Text size="sm" c="gray.7" mb="xs">
                    {t('publish.porters.test_result', '测试结果')}
                  </Text>
                  <Textarea
                    value={portersTestResult}
                    readOnly
                    autosize
                    minRows={4}
                    maxRows={12}
                    styles={{ input: { fontFamily: 'monospace', fontSize: '12px' } }}
                  />
                </Box>
              )}

              {/* 操作按钮 */}
              <Group justify="space-between" mt="md">
                <Group gap="xs">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleTestPorters}
                    loading={testing}
                    disabled={!portersConfig}
                    title={!portersConfig ? t('publish.porters.test_disabled_hint', '先保存配置后再测试') : undefined}
                  >
                    {t('publish.porters.test', '测试连接')}
                  </Button>
                  {portersConfig != null && portersConfig.status !== 'active' && (
                    <Button variant="filled" color="violet" size="sm" onClick={handleConnectPorters}>
                      {t('publish.porters.connect', '连接到 PORTERS →')}
                    </Button>
                  )}
                </Group>
                <Group gap="xs">
                  <Button variant="default" size="sm" onClick={closePortersModal}>
                    {t('publish.actions.cancel')}
                  </Button>
                  <Button type="submit" size="sm" loading={submitting}>
                    {isEditingPorters ? t('publish.actions.update') : t('publish.porters.save', '保存')}
                  </Button>
                </Group>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      {/* 删除确认弹窗 */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title={t('publish.delete_confirmation.title')}
        size="sm"
        centered
      >
        <Stack gap="lg">
          <Text size="sm" c="gray.7">
            {t('publish.delete_confirmation.message', { platform: deleteTarget?.platform?.toUpperCase() })}
          </Text>
          {deleteTarget?.platform === 'zendesk' && existingZendesk && (
            <Alert color="yellow" variant="light" p="xs">
              <Text size="xs">
                {t('publish.zendesk.delete_scope', {
                  bots: existingZendesk.dependencies?.bots || 0,
                  datasets: existingZendesk.dependencies?.datasets || 0,
                })}
              </Text>
              <Text size="xs" mt={4}>
                {t('publish.zendesk.delete_hint_pause')}
              </Text>
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="light" color="gray" onClick={closeDeleteModal}>
              {t('publish.actions.cancel')}
            </Button>
            <Button color="red" onClick={confirmDeleteIntegration}>
              {t('publish.actions.delete')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};
